import { nanoid } from "@reduxjs/toolkit";

/**
 * Drawer Middleware Utilities
 *
 * This file contains utilities for the drawer system middleware which handles
 * the opening, closing, and stacking of drawers in the application.
 *
 * Due to limitations with Redux Toolkit's metadata handling in async thunks,
 * we use a custom approach to track drawer actions across the entire thunk lifecycle.
 */

// Enable/disable detailed debugging for drawer middleware
export const DEBUG_STACKABLE_DRAWER_MIDDLEWARE = false;

// The two possible drawer action types
export type DrawerActionType = "show" | "hide";

/**
 * Detects if an action is a drawer "show" action
 *
 * This handles both:
 * 1. Regular actions with isDrawerAction: "show" metadata
 * 2. Thunk actions that have a requestId starting with "drawer_show_"
 *    (Only fulfilled thunks trigger drawer effects)
 */
export const isDrawerShowAction = (action: any): boolean => {
    // For thunk actions (fulfilled, pending, rejected)
    if (action.meta?.requestStatus) {
        if (
            action.meta?.requestStatus === "fulfilled" &&
            action.meta?.requestId?.startsWith("drawer_show_")
        ) {
            return true;
        } else {
            return false;
        }
    }
    // For regular actions
    if (action.meta?.isDrawerAction === "show") {
        return true;
    }
    return false;
};

/**
 * Detects if an action is a drawer "hide" action
 *
 * This handles both:
 * 1. Regular actions with isDrawerAction: "hide" metadata
 * 2. Thunk actions that have a requestId starting with "drawer_hide_"
 *    (Only fulfilled thunks trigger drawer effects)
 */
export const isDrawerHideAction = (action: any): boolean => {
    // For thunk actions (fulfilled, pending, rejected)
    if (action.meta?.requestStatus) {
        if (
            action.meta?.requestStatus === "fulfilled" &&
            action.meta?.requestId?.startsWith("drawer_hide_")
        ) {
            return true;
        } else {
            return false;
        }
    }
    // For regular actions
    if (action.meta?.isDrawerAction === "hide") {
        return true;
    }
    return false;
};

/**
 * WORKAROUND: Redux Toolkit metadata issue
 *
 * There's a critical bug with Redux Toolkit's metadata handling where:
 * - getPendingMeta correctly adds metadata to pending actions
 * - getFulfilledMeta DOES NOT add metadata to fulfilled actions
 *
 * Since our drawer middleware needs to run on fulfilled actions (after state is updated),
 * we encode the drawer action type directly in the requestId which is guaranteed
 * to persist across the entire action lifecycle.

 */
export function createDrawerThunkMeta(actionType: DrawerActionType) {
    return {
        // Use a custom idGenerator to encode drawer action type in the ID
        idGenerator: () => {
            // Create an ID that includes drawer action type
            return `drawer_${actionType}_${nanoid()}`;
        },
    };
}

/**
 * Creates metadata for regular (non-thunk) drawer actions
 */
export function createDrawerActionMeta(actionType: DrawerActionType) {
    return {
        isDrawerAction: actionType,
    };
}

/**
 * Detects if an action is a pending drawer "show" action
 *
 * Used by the drawer snapshot middleware to save drawer state
 * before a new drawer is shown
 */
export function isDrawerShowActionOrPending(action: any): boolean {
    // For thunk actions (fulfilled, pending, rejected)
    if (action.meta?.requestStatus) {
        if (
            action.meta?.requestStatus === "pending" &&
            action.meta?.requestId?.startsWith("drawer_show_")
        ) {
            return true;
        } else {
            return false;
        }
    }
    // For regular actions
    if (action.meta?.isDrawerAction === "show") {
        return true;
    }
    return false;
}
